import { SimpleDialog } from "@ttc3k/trekker";
import { useAppContext } from "useAppContext";
import { WarningTriangle } from "iconoir-react";
import React, { PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-system/jsx";
import { JsxStyleProps } from "styled-system/types";

type NavigationBlockProps = {
  onStayHereClick?: () => void;
  onContinueClick?: () => void;
} & JsxStyleProps &
  PropsWithChildren;

export const NavigationBlocker = ({
  onStayHereClick = () => {},
  onContinueClick,
  children,
  ...rest
}: NavigationBlockProps) => {
  const { t } = useTranslation();
  const { navigationBlockerDetails, setNavigationBlockerDetails } =
    useAppContext();

  const [isBlockerOpen, setIsBlockerOpen] = useState(false);
  const [clonedEvent, setClonedEvent] = useState<MouseEvent>();
  const [eventTarget, setEventTarget] = useState<EventTarget>();

  const handleClick = (
    e: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
  ) => {
    if (navigationBlockerDetails.isNavigationBlocked) {
      const { target, nativeEvent } = e;
      e.preventDefault();
      e.stopPropagation();

      setEventTarget(target);
      setClonedEvent(new MouseEvent("click", nativeEvent));
      nativeEvent.preventDefault();
      nativeEvent.stopImmediatePropagation();
      nativeEvent.stopPropagation();
      setIsBlockerOpen(true);
      // If all of the event blocking above doesn't stop the event, this will
      return false;
    }
  };

  const handleCancelClick = () => {
    setIsBlockerOpen(false);
    setNavigationBlockerDetails(prev => ({
      ...prev,
      isNavigationBlockerDismissed: true,
    }));
    setTimeout(() => onStayHereClick?.(), 10);
  };

  const handleConfirmClick = () => {
    setIsBlockerOpen(false);
    setNavigationBlockerDetails({
      isNavigationBlocked: false,
      isNavigationBlockerDismissed: true,
    });
    setTimeout(() => {
      if (onContinueClick) {
        onContinueClick();
        return;
      }

      eventTarget?.dispatchEvent(clonedEvent as MouseEvent);
    }, 10);
  };

  return (
    <>
      {/* To avoid a height being set on the div, lineHeight is set to 0 and the display is set to inline-block */}
      <styled.div
        className="navigation-blocker"
        {...rest}
        display="inline-block"
        lineHeight={0}
        onClickCapture={e => handleClick(e)}
      >
        {children}
      </styled.div>
      <SimpleDialog
        open={isBlockerOpen}
        Icon={WarningTriangle}
        title={t("core:UNSAVED_CHANGES.TITLE")}
        message={t("core:UNSAVED_CHANGES.DESCRIPTION")}
        cancelButtonCopy={t("core:UNSAVED_CHANGES.STAY")}
        onCancelClick={handleCancelClick}
        confirmButtonCopy={t("core:UNSAVED_CHANGES.CONTINUE")}
        confirmButtonVisual="destructive"
        onConfirmClick={handleConfirmClick}
        size="md"
      />
    </>
  );
};
